import { useState } from 'react';
import { api } from '../api.js';
import { Card, SectionHeader, Button } from './ui';
import { useAiCache, generatedAgo } from '../hooks/useAiCache.js';

// Phase 4 item 3 — on-demand AI retrospective. The operator picks a period and
// clicks Generate; the backend computes the funnel deterministically and Claude
// narrates ONLY those numbers (every line cites a raw count). Nothing auto-runs,
// so no tokens are spent until asked. Empty periods skip the LLM server-side.

const PERIODS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
];

export default function AnalyticsRetroCard() {
  const [period, setPeriod] = useState('month');
  const [status, setStatus] = useState('idle'); // idle | loading | done | error
  // T262 — survives a navigation; the operator paid for this result once.
  const { data, generatedAt, save } = useAiCache('retro', period);
  const [error, setError] = useState(null);

  const generate = async () => {
    setStatus('loading');
    setError(null);
    try {
      const res = await api.post('/analytics/retro', { period });
      save(res);
      setStatus('done');
    } catch (e) {
      setError(e?.body?.message || e?.message || 'Could not generate the retro.');
      setStatus('error');
    }
  };

  const loading = status === 'loading';
  return (
    <section className="space-y-2">
      <SectionHeader
        title="AI retrospective"
        hint="An on-demand, plain-language recap of a period — written from your real numbers, every line citing its raw count. Uses your Anthropic key (a few cents); nothing runs until you ask."
      />
      <Card padding="md">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1.5">
            {PERIODS.map((p) => (
              <Button
                key={p.value}
                variant={period === p.value ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setPeriod(p.value)}
                disabled={loading}
              >
                {p.label}
              </Button>
            ))}
          </div>
          <Button variant="primary" size="sm" onClick={generate} disabled={loading}>
            {loading ? 'Generating…' : data ? 'Regenerate' : 'Generate retro'}
          </Button>
          {data && generatedAt && !loading && (
            <span className="text-[10px] text-slate-500">generated {generatedAgo(generatedAt)}</span>
          )}
        </div>

        {status === 'error' && <p className="mt-3 text-sm text-red-400">{error}</p>}

        {!loading && data?.empty && (
          <p className="mt-3 text-sm text-slate-400">No applications in this period yet — apply to a few and come back.</p>
        )}

        {!loading && data && !data.empty && (
          <div className="mt-3 space-y-3">
            <p className="text-sm leading-relaxed text-slate-200">{data.summary}</p>
            {data.wins?.length > 0 && (
              <ul className="space-y-1">
                {data.wins.map((w, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
                    <span className="text-slate-300">{w}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.watchOuts?.length > 0 && (
              <ul className="space-y-1">
                {data.watchOuts.map((w, i) => (
                  <li key={i} className="flex gap-2 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
                    <span className="text-slate-300">{w}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.focus && (
              <div className="rounded-md border border-brand-700 bg-brand-600/15 px-3 py-2 text-sm text-slate-200">
                <span className="text-[10px] uppercase tracking-wide text-brand-500">Focus next</span>
                <p className="mt-0.5">{data.focus}</p>
              </div>
            )}
            <p className="text-[10px] text-slate-600">
              Generated by {data.model} · {(data.tokensIn ?? 0) + (data.tokensOut ?? 0)} tokens. The numbers are computed
              deterministically; the AI only narrates them.
            </p>
          </div>
        )}
      </Card>
    </section>
  );
}
